import { Box, Button, IconButton, PopoverMenu } from '@wix/design-system';
import { MoreSmall } from '@wix/wix-ui-icons-common';
import React, { useContext } from 'react';

import { RicosContext } from './../../../ricos-context';

import {
  AI_MODAL_RESULTS_ACTIONS_POPOVER,
  AI_MODAL_RESULTS_ACTIONS_POPOVER_TRIGGER,
  AI_MODAL_RESULTS_FIST_ACTION_BUTTON,
  AI_MODAL_RESULTS_SECOND_ACTION_BUTTON,
} from '../../data-hooks';
import { isOfType, unreachable } from '../../ts-utils';
import type { ResultEntry, ResultsActionsCollection } from '../../types';

type ResultsFooterProps = {
  actions: ResultsActionsCollection;
  zIndex: number;
  modalRef: React.RefObject<any | null>;
  entry: ResultEntry;
};

export const ResultsFooter: React.FC<ResultsFooterProps> = ({
  actions,
  zIndex,
  modalRef,
  entry,
}) => {
  const { t } = useContext(RicosContext);

  const [firstAction, secondAction, ...restActions] = React.useMemo(() => {
    const firstAction = actions.find(action => action.type === 'action');
    const secondAction = actions.find(action => action.type === 'action' && action !== firstAction);
    const restActions = actions.filter(action => action !== firstAction && action !== secondAction);
    return [firstAction, secondAction, ...restActions];
  }, [...actions]);

  return (
    <Box
      paddingTop="SP2"
      paddingBottom="SP2"
      paddingLeft="SP4"
      paddingRight="SP4"
      align="space-between"
    >
      <Box gap="tiny">
        {firstAction && isOfType('action', firstAction) && (
          <Button
            dataHook={AI_MODAL_RESULTS_FIST_ACTION_BUTTON}
            size="small"
            priority="secondary"
            prefixIcon={firstAction.icon ? <firstAction.icon /> : undefined}
            onClick={() => {
              firstAction.onClick(entry);
            }}
          >
            {t(firstAction.title)}
          </Button>
        )}
        {secondAction && isOfType('action', secondAction) && (
          <Button
            dataHook={AI_MODAL_RESULTS_SECOND_ACTION_BUTTON}
            size="small"
            priority="secondary"
            prefixIcon={secondAction.icon ? <secondAction.icon /> : undefined}
            onClick={() => {
              secondAction.onClick(entry);
            }}
          >
            {t(secondAction.title)}
          </Button>
        )}
        {!!modalRef.current && (
          <PopoverMenu
            dataHook={AI_MODAL_RESULTS_ACTIONS_POPOVER}
            triggerElement={
              <IconButton
                dataHook={AI_MODAL_RESULTS_ACTIONS_POPOVER_TRIGGER}
                size="small"
                priority="secondary"
              >
                <MoreSmall />
              </IconButton>
            }
            textSize="small"
            maxHeight={250}
            zIndex={zIndex + 1}
            appendTo={modalRef.current}
          >
            {restActions.map(action => {
              switch (action.type) {
                case 'action': {
                  return (
                    <PopoverMenu.MenuItem
                      dataHook={action.title}
                      key={action.title}
                      text={t(action.title)}
                      prefixIcon={action.icon ? <action.icon /> : undefined}
                      onClick={() => {
                        action.onClick(entry);
                      }}
                    />
                  );
                }
                case 'section': {
                  return <PopoverMenu.SectionTitle key={action.title} title={t(action.title)} />;
                }
                default: {
                  throw unreachable(action);
                }
              }
            })}
          </PopoverMenu>
        )}
      </Box>
    </Box>
  );
};
